// src/services/claudeApi.js
// Claude AI — 마케팅 메시지 초안 자동 생성

const API_KEY = process.env.REACT_APP_CLAUDE_API_KEY;
const TIMEOUT_MS = 30_000;

function fetchWithTimeout(url, options) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  return fetch(url, { ...options, signal: controller.signal })
    .finally(() => clearTimeout(timer));
}

/**
 * 재방문 홍보 메시지 초안 생성
 * @param {object} opts
 * @param {string} opts.promoName   - 홍보 유형 이름 (예: "바비큐 무료")
 * @param {string} opts.promoIcon   - 아이콘 (예: "🥩")
 * @param {string} opts.season      - 계절 (예: "봄")
 * @param {object[]} opts.customers - 대상 고객 배열
 * @returns {Promise<string>}       - 생성된 메시지 (이름 자리에 {이름} 포함)
 */
export async function generateMarketingDraft({ promoName, promoIcon, season, customers }) {
  if (!API_KEY) throw new Error('NO_API_KEY');

  const MS_PER_DAY = 86_400_000;

  // 고객 맥락 요약
  const customersWithVisit = customers.filter(c => c.lastVisit);
  const avgDays = customersWithVisit.length > 0
    ? Math.round(
        customersWithVisit.reduce((sum, c) =>
          sum + Math.floor((Date.now() - new Date(c.lastVisit).getTime()) / MS_PER_DAY), 0
        ) / customersWithVisit.length
      )
    : 0;
  const hasGroup   = customers.some(c => c.badges?.includes('단체') || (c.guests || 0) >= 8);
  const hasRegular = customers.some(c => c.badges?.includes('단골') || c.visits >= 3);
  const hasAtRisk  = customers.some(c => {
    if (!c.lastVisit) return false;
    return Math.floor((Date.now() - new Date(c.lastVisit).getTime()) / MS_PER_DAY) >= 120;
  });

  // 이용 서비스 통계
  const withBehavior = customers.filter(c =>
    c.used_bbq !== undefined || c.used_firewood !== undefined || c.used_pool !== undefined
  );
  const bbqRate      = withBehavior.length > 0
    ? Math.round(withBehavior.filter(c => c.used_bbq).length / withBehavior.length * 100) : null;
  const firewoodRate = withBehavior.length > 0
    ? Math.round(withBehavior.filter(c => c.used_firewood).length / withBehavior.length * 100) : null;
  const poolRate     = withBehavior.length > 0
    ? Math.round(withBehavior.filter(c => c.used_pool).length / withBehavior.length * 100) : null;

  const behaviorParts = [
    bbqRate !== null      ? `바베큐 이용률 ${bbqRate}%` : null,
    firewoodRate !== null ? `장작 이용률 ${firewoodRate}%` : null,
    poolRate !== null     ? `수영장 이용률 ${poolRate}%` : null,
  ].filter(Boolean);

  const contextParts = [
    `평균 ${avgDays}일 미방문`,
    hasGroup   ? '단체 고객 포함' : null,
    hasRegular ? '단골 고객 포함' : null,
    hasAtRisk  ? '이탈 위험 고객 포함' : null,
  ].filter(Boolean).join(', ');

  const behaviorContext = behaviorParts.length > 0
    ? `\n과거 이용 서비스 데이터: ${behaviorParts.join(', ')}`
    : '';

  // 이용률이 높은 서비스를 문구에 자연스럽게 언급하도록 힌트 제공
  const behaviorHint = (() => {
    if (bbqRate !== null && bbqRate >= 50) return '- 이 고객들은 바베큐를 많이 이용했으므로 바베큐 관련 문구를 자연스럽게 활용하세요';
    if (poolRate !== null && poolRate >= 50) return '- 이 고객들은 수영장/온수풀을 즐겼으므로 해당 시설 관련 문구를 자연스럽게 활용하세요';
    if (firewoodRate !== null && firewoodRate >= 50) return '- 이 고객들은 장작을 자주 이용했으므로 캠프파이어 분위기 문구를 자연스럽게 활용하세요';
    return '';
  })();

  const prompt = `당신은 숙소(펜션) 재방문 홍보 문자 작성 전문가입니다.
아래 정보를 바탕으로 바로 발송 가능한 홍보 문자를 작성해주세요.

홍보 유형: ${promoIcon} ${promoName}
현재 계절: ${season}
대상 고객 특성: ${contextParts}${behaviorContext}

작성 조건:
- 첫 줄은 반드시 "안녕하세요 {이름} 고객님 😊"로 시작 (이름 자리는 {이름} 그대로)
- 4~6문장 이내, 짧고 자연스럽게
- 혜택이 명확히 보이도록
- 광고 느낌 과하지 않게, 지인처럼 편한 톤
- 문자 발송용이므로 마크다운 없이 일반 텍스트로
- 끝인사 포함${behaviorHint ? '\n' + behaviorHint : ''}

문자 내용만 출력하고 다른 설명은 쓰지 마세요.`;

  const res = await fetchWithTimeout('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': API_KEY,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-allow-browser': 'true',
    },
    body: JSON.stringify({
      model: 'claude-haiku-4-5-20251001',
      max_tokens: 400,
      messages: [{ role: 'user', content: prompt }],
    }),
  });

  if (!res.ok) throw new Error(`API_ERROR_${res.status}`);

  const json = await res.json();
  const text = json.content?.[0]?.text?.trim();
  if (!text) throw new Error('EMPTY_RESPONSE');
  return text;
}

/**
 * 기존 문구 → 스테이메이트 템플릿 변환
 * @param {string} originalText   - 운영자가 붙여넣은 기존 문자 내용
 * @param {'short'|'friendly'} tone - 짧게 정리 | 친절하게 정리
 * @param {string} templateType   - 'checkin' | 'checkout' | 'reservation' 등
 * @returns {Promise<string>}     - 변수 포함 템플릿 문자열
 */
export async function generateTemplateFromExisting({ originalText, tone, templateType = 'checkin' }) {
  if (!API_KEY) throw new Error('NO_API_KEY');

  const toneDesc = tone === 'short'
    ? '핵심만 남겨 간결하게 3~5문장으로 정리'
    : '친절하고 따뜻한 말투를 유지하며 자연스럽게 정리';

  const typeDesc = {
    checkin:     '체크인(입실) 안내',
    checkout:    '체크아웃(퇴실) 안내',
    reservation: '예약 확인',
    payment:     '입금 확인',
    mannertime:  '매너타임 안내',
    review:      '리뷰 요청',
  }[templateType] || '안내';

  const prompt = `당신은 숙소(펜션) 운영자를 돕는 메시지 템플릿 전문가입니다.
아래는 숙소 운영자가 실제로 쓰고 있는 "${typeDesc}" 문자 내용입니다.
이 문장을 스테이메이트 앱의 템플릿 형식으로 변환해주세요.

[기존 문자 내용]
${originalText}

[변환 조건]
- 말투와 분위기는 원본을 최대한 유지하세요
- ${toneDesc}
- 아래 변수를 적절한 위치에 삽입하세요 (원본에 해당 내용이 있을 때만):
  · {이름} — 고객 이름 (예: "홍길동님" → "{이름}님")
  · {객실명} — 객실 이름
  · {입실시간} — 체크인 시간 (예: "오후 3시", "15:00" 등)
  · {퇴실시간} — 체크아웃 시간
  · {체크인날짜} — 체크인 날짜
  · {안내사항} — 주차/와이파이 등 부가 안내 (원본에 고정 안내 문구가 있으면 그대로 유지해도 됨)
  · {숙소명} — 숙소 이름
  · {문의전화} — 문의 전화번호
- 마크다운 없이 일반 텍스트로만 출력
- 변환된 템플릿 내용만 출력하고 설명은 쓰지 마세요`;

  const res = await fetchWithTimeout('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': API_KEY,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-allow-browser': 'true',
    },
    body: JSON.stringify({
      model: 'claude-haiku-4-5-20251001',
      max_tokens: 600,
      messages: [{ role: 'user', content: prompt }],
    }),
  });

  if (!res.ok) throw new Error(`API_ERROR_${res.status}`);
  const json = await res.json();
  const text = json.content?.[0]?.text?.trim();
  if (!text) throw new Error('EMPTY_RESPONSE');
  return text;
}
